/*
  Загрузка выгрузки 1С - два файла вместо одного.

  Заказчик отдаёт помесячные начисления по услугам отчётом «Отчёт по начислениям
  и долгам»: в нём лицевой счёт, месяц, услуга и суммы, и больше ничего. Адреса и
  фамилий там нет по устройству - отчёт строится по регистру взаиморасчётов, а
  это реквизиты справочника. Их даёт второй отчёт, «Сведения о задолженностях по
  лицевым счетам». Оба свёрнуты для чтения человеком, и склеивает их сервер;
  экран об этом не знает, он только собирает файлы и показывает, что получилось.

  🔴 С 30.09.2026 второй файл нужен почти только ради ТЕЛЕФОНОВ: адрес, Ф.И.О. и
  вид собственности приносит шаг 1 - реестр должников. Подпись поля об этом
  говорит прямо, иначе человек ищет в 1С отчёт ради сведений, которые у него уже
  загружены.

  Памятка «где это взять» стоит НА экране, а не в инструкции сбоку: 16.09.2026
  выяснилось, что специалист заказчика не нашёл нужный отчёт в своей же базе и
  сказал, что такого в программе нет. Экран, который не называет отчёт и путь к
  нему, эту историю повторит у каждого оператора.

  Срок внесения платы здесь не поле, а факт со ссылкой в настройки: он один на
  организацию и берётся из договора управления. Поле при каждой загрузке давало
  бы возможность ошибиться заново, а ошибка в нём тихо портит и пени, и исковую
  давность по всему портфелю.
*/

import { useState } from 'react'

import { api } from '../api/client.js'
import { accounts } from '../api/format.js'
import { Notice } from './Common.jsx'
import Disclosure from './Disclosure.jsx'
import ImportOutcome from './ImportOutcome.jsx'
import OneCSummary from './OneCSummary.jsx'
import NameDifferences from './picker/NameDifferences.jsx'
import PickAccounts from './picker/PickAccounts.jsx'
import UploadStages from './upload/UploadStages.jsx'
import useUploadFlow from './upload/useUploadFlow.js'

const WHERE = [
  {
    key: 'charges',
    title: 'Начисления по услугам',
    report: 'Отчёт по начислениям и долгам',
    path: '«Начисления услуг» → «Отчёты»',
    how: 'Период - весь период долга. Отбор по лицевым счетам волны, остальные отборы снять. Сохранить значком дискеты, тип файла «Лист Excel (xlsx)». Разворачивать группировки не нужно.',
  },
  /*
    🔴 30.09.2026: подпись второго файла изменена по указанию владельца, и это не
    косметика. Отчёт даёт четыре графы - адрес, Ф.И.О., телефон и вид
    собственности. Три из них с этого дня приносит шаг 1, реестр должников, и
    подпись «Реквизиты лицевых счетов» обещала работу, которая уже сделана:
    человек искал в 1С второй отчёт ради того, что у него уже загружено.

    Осталась ровно одна графа, которой в реестре нет, - телефон. Поле поэтому не
    убрано: без коннектора это единственный путь, которым телефоны попадают в
    систему, а без них не идёт обзвон.
  */
  {
    key: 'details',
    title: 'Телефоны жильцов',
    report: 'Сведения о задолженностях по лицевым счетам',
    path: 'поиск по названию отчёта',
    how: 'Нужен только ради телефонов: адрес, Ф.И.О. и вид собственности уже пришли реестром на шаге 1. Без телефонов не пойдёт обзвон, но начисления загрузятся и без этого файла. Сохранить так же, в xlsx.',
  },
]

/*
  🔴 С 19.10.2026 оба файла уходят на сервер ОДИН раз (указание владельца):
  выбор файла отправляет его, разбор идёт фоновой работой с ходом, а
  «Посмотреть», окно счетов и запись называют уже прочитанные файлы. Запись -
  фоновой работой со счётчиком строк (`useUploadFlow`, «Ход загрузки»).
*/
function useFiles() {
  const charges = useUploadFlow('onec_charges')
  const details = useUploadFlow('onec_details')
  const flows = { charges, details }
  // Второй файл необязателен, но если выбран - ждём, пока он прочитается.
  const ready = Boolean(charges.stored) && (details.phase === 'idle' || Boolean(details.stored))
  return { flows, ready, charges: charges.stored, details: details.stored }
}

export default function OneCUpload({ dueDay, onLoaded, onEditDueDay }) {
  const files = useFiles()
  const [summary, setSummary] = useState(null)
  // Окно «Какие счета загрузить» (16.10.2026): перечень - в сводке, пусто - весь отчёт.
  const [only, setOnly] = useState(null)
  const [outcome, setOutcome] = useState(null)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)
  // Номер захода: «Загрузить другой файл» пересоздаёт поля выбора, иначе в них
  // осталось бы имя прежнего файла, а тот же файл нельзя было бы выбрать снова.
  const [round, setRound] = useState(0)

  function choose(key, file) {
    files.flows[key].choose(file ?? null)
    setSummary(null)
    setOnly(null)
    setOutcome(null)
    setError(null)
  }

  function again() {
    choose('charges', null)
    choose('details', null)
    setRound(round + 1)
  }

  const flowBusy = files.flows.charges.busy || files.flows.details.busy
  const stop = busy || flowBusy || !files.ready

  // Возвращает «получилось»: кнопке выбора счетов нужно знать, открывать ли окно.
  async function act(action, then) {
    setBusy(true)
    setError(null)
    try {
      then(await action())
      return true
    } catch (failure) {
      setError(failure)
      return false
    } finally {
      setBusy(false)
    }
  }

  const look = () =>
    act(
      () =>
        files.flows.charges.check(
          () => api.previewOnec(files.charges, files.details, only),
          (seen) => `счетов ${seen.accounts}, услуг ${seen.services}, строк ${seen.rows}`,
        ),
      (seen) => setSummary(seen),
    )

  // Другой выбор - другая сводка и погашение только у ВЫБРАННЫХ счетов.
  const pick = (numbers) => {
    setOnly(numbers)
    act(() => api.previewOnec(files.charges, files.details, numbers), setSummary)
  }

  /*
    🔴 С 19.10.2026 подтверждать объём больше нечем и незачем (решение
    владельца): сторож объёма не останавливает загрузку, а при выгрузке «в разы
    меньше прошлой» откладывает погашение пропавших месяцев - решение стоит в
    сводке под итогом (`OneCSettled`). Кнопка «Объём ожидаем, загрузить»
    появилась здесь 30.09.2026 и ушла вместе с остановкой.
  */
  const load = () =>
    act(
      () =>
        files.flows.charges.write(
          'onec',
          { only_accounts: only ? JSON.stringify(only) : null },
          files.details,
        ),
      (written) => {
        setOutcome(written)
        setSummary({ ...written.summary, choices: summary?.choices })
        onLoaded(written.import_run_id)
      },
    )

  return (
    <>
      {/* Памятка свёрнута под кнопку с её же заголовком - указание владельца
          18.09.2026, одно на все памятки раздела (`Disclosure.jsx`). Сам текст
          остался дословно: он появился 16.09 после того, как специалист
          заказчика не нашёл отчёт в своей же базе. */}
      <Disclosure title="Где это взять в 1С">
        <ol className="onec__where">
          {WHERE.map((item) => (
            <li key={item.key}>
              <b>{item.title}</b> - отчёт «{item.report}», {item.path}. {item.how}
            </li>
          ))}
        </ol>
      </Disclosure>

      <section className="section card">
        {WHERE.map((item) => (
          <div className="field field--l" key={`${item.key}-${round}-${files.flows[item.key].attempt}`}>
            <label className="field__control">
              <span className="field__label">
                {item.title}
                {item.key === 'details' ? ' (необязательно)' : ''}
              </span>
              <input
                className="field__file"
                type="file"
                accept=".xlsx"
                disabled={busy || files.flows[item.key].busy}
                onChange={(event) => choose(item.key, event.target.files?.[0])}
              />
            </label>
          </div>
        ))}

        <p className="onec__due">
          Срок оплаты: {dueDay ? <b>{dueDay}-е число следующего месяца</b> : <b>не задан</b>}.{' '}
          <button type="button" className="button button--quiet" onClick={onEditDueDay}>
            Изменить
          </button>
        </p>

        <div className="row">
          <button type="button" className="button" disabled={stop || Boolean(outcome)} onClick={look}>
            Посмотреть, что получится
          </button>
          <PickAccounts
            choices={summary?.choices ?? null}
            only={only}
            onChange={pick}
            onNeed={look}
            disabled={stop || Boolean(outcome)}
            note={
              files.charges
                ? `${files.charges.name} · счетов в отчёте: ${summary?.choices?.length ?? '…'}`
                : null
            }
            amountTitle="Долг по отчёту"
          />
          <button
            type="button"
            className="button"
            disabled={stop || !summary || Boolean(outcome)}
            onClick={load}
          >
            {only ? `Загрузить выбранные: ${accounts(only.length)}` : 'Загрузить'}
          </button>
        </div>
        {outcome ? null : <NameDifferences choices={summary?.choices} />}
        <UploadStages flow={files.flows.charges} />
        <UploadStages flow={files.flows.details} title="Телефоны жильцов" short />
        <Notice error={files.flows.charges.error ?? files.flows.details.error ?? error} />
        {summary ? (
          <OneCSummary summary={summary} outcome={outcome} picked={Boolean(only)} />
        ) : null}
      </section>

      {/* Итог загрузки - тем же блоком, что у загрузки файлом: он перечисляет
          строки с замечаниями, и второй его вид разошёлся бы с первым. */}
      {outcome ? (
        <ImportOutcome
          outcome={outcome}
          accounts={outcome.summary?.accounts}
          seconds={files.flows.charges.written?.seconds}
          onAgain={again}
        />
      ) : null}
    </>
  )
}
